import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { StatusBadge, Surface, SurfaceHeader } from "../components/PortalPrimitives";
import { fetchDocumentsBrowse } from "../lib/api";
import type { BrowseDocument, ChipTone } from "../lib/types";

type DocumentFilters = {
  q: string;
  ticker: string;
  source: string;
};

const EMPTY_FILTERS: DocumentFilters = { q: "", ticker: "", source: "" };

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "UTC",
});

function parseDate(value: string | null): Date | null {
  if (!value) return null;
  const parsed = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function displayDate(value: string | null): string {
  const parsed = parseDate(value);
  return parsed ? dateFormatter.format(parsed) : value || "Date unavailable";
}

function displayTime(value: string | null): string | null {
  const parsed = parseDate(value);
  return parsed ? timeFormatter.format(parsed) : null;
}

function displayLabel(value: string | null, fallback = "Unavailable"): string {
  if (!value) return fallback;
  const normalized = value.replace(/[_-]+/g, " ").trim();
  return normalized
    ? `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`
    : fallback;
}

function qualityTone(value: string | null): ChipTone {
  const normalized = value?.toLowerCase() ?? "";
  if (["ok", "ready", "complete", "completed", "verified"].includes(normalized)) {
    return "green";
  }
  if (["warning", "partial", "review", "needs_review"].includes(normalized)) {
    return "amber";
  }
  if (["error", "failed", "rejected"].includes(normalized)) return "red";
  return "gray";
}

function viewerPath(document: BrowseDocument): string {
  if (document.deep_link?.startsWith("/app")) {
    return document.deep_link.replace(/^\/app/, "") || "/";
  }
  return `/documents/${encodeURIComponent(document.document_id)}`;
}

function DocumentRow({ document }: { document: BrowseDocument }) {
  const source = document.source_name || document.provider || document.source_type;
  const title = document.title || document.external_document_id || "Untitled document";
  const quality = displayLabel(document.quality_status, "Not rated");

  return (
    <tr>
      <td className="documents-cell documents-cell--ingested">
        <time dateTime={document.ingested_at ?? undefined}>
          <span>{displayDate(document.ingested_at)}</span>
          {displayTime(document.ingested_at) ? <small>{displayTime(document.ingested_at)}</small> : null}
        </time>
      </td>
      <td className="documents-cell documents-cell--company">
        <strong>{document.ticker || "—"}</strong>
        <span>{document.company_name || "Company unavailable"}</span>
      </td>
      <td className="documents-cell documents-cell--document">
        <Link to={viewerPath(document)}>{title}</Link>
        {document.title && document.external_document_id ? (
          <span className="documents-document-id">{document.external_document_id}</span>
        ) : null}
      </td>
      <td className="documents-cell documents-cell--source">
        <strong>{displayLabel(source, "Source unavailable")}</strong>
        <span>{displayLabel(document.doc_type, "Type unavailable")}</span>
      </td>
      <td className="documents-cell documents-cell--date">
        <time dateTime={document.doc_date ?? undefined}>{displayDate(document.doc_date)}</time>
      </td>
      <td className="documents-cell documents-cell--coverage">
        <strong>{document.page_count === null ? "—" : `${document.page_count} pp`}</strong>
        <span>{document.chunk_count === null ? "Chunks unavailable" : `${document.chunk_count} chunks`}</span>
      </td>
      <td className="documents-cell documents-cell--quality">
        <StatusBadge tone={qualityTone(document.quality_status)}>{quality}</StatusBadge>
      </td>
    </tr>
  );
}

// Browse GET /api/portal/documents using exactly the filters supported by the
// backend route (q / ticker / source / page), with no speculative facets.
export default function Documents() {
  const [draft, setDraft] = useState<DocumentFilters>(EMPTY_FILTERS);
  const [active, setActive] = useState({ ...EMPTY_FILTERS, page: 1 });

  const documents = useQuery({
    queryKey: ["documents-browse", active],
    queryFn: () => fetchDocumentsBrowse(active),
    placeholderData: (previousData) => previousData,
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setActive({
      q: draft.q.trim(),
      ticker: draft.ticker.trim().toUpperCase(),
      source: draft.source.trim(),
      page: 1,
    });
  }

  function clearFilters() {
    setDraft(EMPTY_FILTERS);
    setActive({ ...EMPTY_FILTERS, page: 1 });
  }

  const data = documents.data;
  const pagination = data?.pagination;
  const activeFilterCount = [active.q, active.ticker, active.source].filter(Boolean).length;
  const firstResult = pagination && pagination.total > 0
    ? (pagination.page - 1) * pagination.page_size + 1
    : 0;
  const lastResult = pagination ? firstResult + data!.count - 1 : 0;

  return (
    <div className="documents-page">
      <header className="documents-pagehead">
        <div>
          <div className="documents-eyebrow">Research corpus</div>
          <h1>Documents</h1>
          <p>Browse the ingested corpus, newest first, with direct access to every document.</p>
        </div>
        <div className="documents-page-actions">
          {pagination ? (
            <StatusBadge tone="green">{pagination.total.toLocaleString()} indexed</StatusBadge>
          ) : null}
          <button
            className="btn documents-refresh"
            type="button"
            disabled={documents.isFetching}
            onClick={() => void documents.refetch()}
          >
            {documents.isFetching && !documents.isLoading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </header>

      <Surface className="documents-filters">
        <form className="documents-filter-form" aria-label="Document filters" onSubmit={submit}>
          <label className="documents-filter-search">
            <span>Search</span>
            <input
              value={draft.q}
              onChange={(event) => setDraft({ ...draft, q: event.target.value })}
              placeholder="Title, company, or keyword"
              aria-label="Search documents"
            />
          </label>
          <label>
            <span>Ticker</span>
            <input
              value={draft.ticker}
              onChange={(event) => setDraft({ ...draft, ticker: event.target.value })}
              placeholder="e.g. MU"
              autoCapitalize="characters"
              aria-label="Filter by ticker"
            />
          </label>
          <label>
            <span>Source</span>
            <input
              value={draft.source}
              onChange={(event) => setDraft({ ...draft, source: event.target.value })}
              placeholder="e.g. SEC or Citi"
              aria-label="Filter by source"
            />
          </label>
          <div className="documents-filter-actions">
            <button className="btn primary" type="submit" disabled={documents.isFetching}>
              Filter
            </button>
            <button
              className="btn"
              type="button"
              disabled={activeFilterCount === 0 && Object.values(draft).every((value) => !value)}
              onClick={clearFilters}
            >
              Clear
            </button>
          </div>
        </form>
      </Surface>

      {documents.isLoading ? (
        <Surface className="documents-state" role="status" aria-live="polite">
          <span className="documents-state-pulse" aria-hidden="true" />
          <div>
            <strong>Loading the document catalog</strong>
            <span>Checking the newest ingested research…</span>
          </div>
        </Surface>
      ) : null}

      {documents.isError ? (
        <Surface className="documents-state documents-state--error" role="alert">
          <div>
            <strong>Documents could not be loaded</strong>
            <span>The rest of Athena is unaffected. Retry the catalog request.</span>
          </div>
          <button className="btn" type="button" onClick={() => void documents.refetch()}>
            Try again
          </button>
        </Surface>
      ) : null}

      {data ? (
        <Surface
          className="documents-catalog"
          aria-labelledby="documents-catalog-title"
          aria-busy={documents.isFetching}
        >
          <SurfaceHeader
            title="Document catalog"
            titleId="documents-catalog-title"
            detail={
              activeFilterCount > 0
                ? `${activeFilterCount} active ${activeFilterCount === 1 ? "filter" : "filters"}`
                : "All sources and companies"
            }
            count={pagination?.total ?? data.count}
          />

          {data.count === 0 ? (
            <div className="documents-empty">
              <strong>
                {activeFilterCount > 0
                  ? "No documents match these filters"
                  : "No documents have been ingested yet"}
              </strong>
              <span>
                {activeFilterCount > 0
                  ? "Try a broader keyword, ticker, or source."
                  : "New documents will appear here as they enter the corpus."}
              </span>
              {activeFilterCount > 0 ? (
                <button className="btn" type="button" onClick={clearFilters}>
                  Clear filters
                </button>
              ) : null}
            </div>
          ) : (
            <div className="documents-table-wrap">
              <table className="documents-table">
                <caption className="ops-visually-hidden">Newest ingested documents</caption>
                <thead>
                  <tr>
                    <th scope="col">Ingested</th>
                    <th scope="col">Company</th>
                    <th scope="col">Document</th>
                    <th scope="col">Source / type</th>
                    <th scope="col">Document date</th>
                    <th scope="col">Coverage</th>
                    <th scope="col">Quality</th>
                  </tr>
                </thead>
                <tbody>
                  {data.documents.map((document) => (
                    <DocumentRow key={document.document_id} document={document} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination && data.count > 0 ? (
            <nav className="documents-pagination" aria-label="Document results pages">
              <span className="documents-result-range" aria-live="polite">
                {firstResult.toLocaleString()}–{lastResult.toLocaleString()} of{" "}
                {pagination.total.toLocaleString()}
              </span>
              <div>
                <button
                  className="btn"
                  type="button"
                  disabled={!pagination.has_previous || documents.isFetching}
                  onClick={() => setActive((current) => ({ ...current, page: current.page - 1 }))}
                >
                  Previous
                </button>
                <span className="documents-page-count">
                  Page {pagination.page} of {pagination.total_pages}
                </span>
                <button
                  className="btn"
                  type="button"
                  disabled={!pagination.has_next || documents.isFetching}
                  onClick={() => setActive((current) => ({ ...current, page: current.page + 1 }))}
                >
                  Next
                </button>
              </div>
            </nav>
          ) : null}
        </Surface>
      ) : null}
    </div>
  );
}
